import type { ReactNode } from 'react';
import { ArrowDown, ArrowUp, Minus, type LucideIcon } from 'lucide-react';
import { cn } from './uiTheme';

const ACCENT_BAR = {
  purple: 'bg-brand-purple',
  teal: 'bg-brand-teal',
  warning: 'bg-amber-500',
  critical: 'bg-red-500',
};

const ACCENT_ICON_COLOR = {
  purple: 'text-brand-purple-light',
  teal: 'text-brand-teal-light',
  warning: 'text-amber-400',
  critical: 'text-red-400',
};

const DELTA_TONE = {
  positive: 'text-emerald-400',
  negative: 'text-red-400',
  neutral: 'text-slate-400',
};

type KpiCardAccent = keyof typeof ACCENT_BAR;

function KpiSparkline({
  values,
  accent,
}: {
  values: number[];
  accent: KpiCardAccent;
}) {
  const finite = values.filter((v) => Number.isFinite(v));
  if (finite.length < 2) return null;

  const max = Math.max(...finite, Number.EPSILON);
  const barColor = ACCENT_BAR[accent] || ACCENT_BAR.purple;

  return (
    <div className="mt-3 flex h-8 items-end gap-1" aria-hidden>
      {finite.map((v, i) => {
        const isRecent = i >= finite.length - 2;
        const heightPct = Math.max(12, Math.round((v / max) * 100));
        return (
          <div
            key={`spark-bar-${heightPct}-${v}`}
            className={cn(
              'min-h-[2px] flex-1 rounded-sm',
              barColor,
              isRecent ? 'opacity-100' : 'opacity-40'
            )}
            style={{ height: `${heightPct}%` }}
          />
        );
      })}
    </div>
  );
}

/**
 * KPI / stat card for dashboards and metrics pages.
 */
export function KpiCard({
  label,
  value,
  delta,
  deltaTone = 'neutral',
  accent = 'purple',
  highlight = false,
  hint,
  icon: Icon,
  sparkline,
  children,
  className = '',
}: {
  label?: string;
  value?: ReactNode;
  delta?: ReactNode;
  deltaTone?: keyof typeof DELTA_TONE;
  accent?: KpiCardAccent;
  highlight?: boolean;
  hint?: string;
  icon?: LucideIcon;
  sparkline?: number[] | null;
  children?: ReactNode;
  className?: string;
}) {
  const DeltaIcon =
    deltaTone === 'positive' ? ArrowUp : deltaTone === 'negative' ? ArrowDown : Minus;
  const iconColor = ACCENT_ICON_COLOR[accent] || ACCENT_ICON_COLOR.purple;
  const sparklineValues = Array.isArray(sparkline) ? sparkline : [];

  return (
    <div
      className={cn(
        'overflow-hidden rounded-xl border border-dark-border bg-dark-surface p-5 transition-all duration-200 hover:border-dark-border-glow',
        className
      )}
    >
      <div
        className={cn(
          '-mx-5 -mt-5 mb-4 h-0.5 w-[calc(100%+2.5rem)]',
          ACCENT_BAR[accent] || ACCENT_BAR.purple
        )}
      />
      {label || Icon ? (
        <div className="flex items-start justify-between gap-2">
          {label ? <p className="text-sm font-medium text-slate-400">{label}</p> : <span />}
          {Icon ? <Icon className={cn('h-3.5 w-3.5 shrink-0', iconColor)} aria-hidden /> : null}
        </div>
      ) : null}
      {value != null && value !== false ? (
        <p
          className={cn(
            'mt-1 text-3xl font-bold',
            highlight
              ? 'bg-gradient-to-r from-brand-purple-light to-brand-teal-light bg-clip-text text-transparent'
              : 'text-slate-100'
          )}
        >
          {value}
        </p>
      ) : null}
      <KpiSparkline values={sparklineValues} accent={accent} />
      {delta != null && delta !== '' ? (
        <p
          className={cn(
            'mt-2 flex items-center gap-1 text-sm',
            DELTA_TONE[deltaTone] || DELTA_TONE.neutral
          )}
        >
          <DeltaIcon className="h-3.5 w-3.5 shrink-0" aria-hidden />
          <span>{delta}</span>
        </p>
      ) : null}
      {hint ? <p className="mt-2 line-clamp-2 text-xs text-slate-500">{hint}</p> : null}
      {children ? <div className="mt-3">{children}</div> : null}
    </div>
  );
}

export default KpiCard;
